// Durable outbox of mutations awaiting sync. Namespaced per authenticated user so
// account switches never cross-contaminate queues. Never deleted silently on failure.
import { storage } from "./storage";

const KEY = (userKey, id) => `${userKey}::${id}`;

export const OUTBOX_STATES = ["pending", "syncing", "synced", "failed_retryable", "failed_permanent", "conflict"];

export function newOperationId() {
  const rnd = (crypto?.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random());
  return String(rnd).replace(/-/g, "").slice(0, 32);
}

// Persisted monotonic counter so FIFO order is deterministic even when several
// entries are queued within the same millisecond (critical for depends_on ordering).
async function nextSeq() {
  const cur = (await storage.get("meta", "outbox_seq")) || 0;
  const n = cur + 1;
  await storage.put("meta", "outbox_seq", n);
  return n;
}

export const outbox = {
  async enqueue(userKey, entry) {
    const rec = {
      operation_id: entry.operation_id || newOperationId(),
      seq: await nextSeq(),
      entity_type: entry.entity_type,
      action: entry.action,
      local_entity_id: entry.local_entity_id || null,
      org_id: entry.org_id, site_id: entry.site_id || null, user_id: entry.user_id,
      user_key: userKey,
      endpoint: entry.endpoint,
      payload: entry.payload || {},
      depends_on: entry.depends_on || [],
      created_at_device: entry.recorded_at_device || new Date().toISOString(),
      device_tz_offset: -new Date().getTimezoneOffset(),
      queued_at: new Date().toISOString(),
      attempt_count: 0, last_attempt_at: null, next_retry_at: null,
      state: "pending", last_safe_error: null,
      server_id: null, synced_at_server: null,
      app_build: window.__cc_build || null,
    };
    await storage.put("outbox", KEY(userKey, rec.operation_id), rec);
    return rec;
  },
  async list(userKey) {
    const all = await storage.all("outbox");
    return all.filter((r) => r.user_key === userKey)
      // FIFO: monotonic seq is authoritative; queued_at is a stable fallback.
      .sort((a, b) => ((a.seq ?? 0) - (b.seq ?? 0)) || a.queued_at.localeCompare(b.queued_at));
  },
  async update(userKey, opId, patch) {
    const cur = await storage.get("outbox", KEY(userKey, opId));
    if (!cur) return null;
    const next = { ...cur, ...patch };
    await storage.put("outbox", KEY(userKey, opId), next);
    return next;
  },
  // Remove a single queue entry. ONLY safe when the record is already durably recorded
  // server-side (e.g. an idempotent duplicate) — never as a generic discard.
  async remove(userKey, opId) {
    await storage.del("outbox", KEY(userKey, opId));
    return true;
  },
  async pendingCount(userKey) {
    const all = await this.list(userKey);
    return {
      pending: all.filter((r) => ["pending", "failed_retryable"].includes(r.state)).length,
      syncing: all.filter((r) => r.state === "syncing").length,
      failed: all.filter((r) => ["failed_permanent", "conflict"].includes(r.state)).length,
      total: all.length,
    };
  },
  // Synced items may be compacted per retention policy (docs); statutory/failed never auto-purged.
  async compactSynced(userKey, keepMs = 24 * 3600 * 1000) {
    const all = await this.list(userKey);
    const cutoff = Date.now() - keepMs;
    for (const r of all) {
      if (r.state === "synced" && r.synced_at_server && Date.parse(r.synced_at_server) < cutoff) {
        await storage.del("outbox", KEY(userKey, r.operation_id));
      }
    }
  },
};
